import { Alignment, Direction, Displaying, Spacing, Text, Typography } from "@bonusx-global/bx-design-system";
import { SearchTextDuotone } from "@bonusx-global/bx-icons";
import styled from "@emotion/styled";
import Grid from "@mui/material/Grid";
import isEmpty from "lodash/isEmpty";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useIsMobile from "../../../../hooks/device/useIsMobile";
import { useMutateSearch } from "../../../hooks/useSearchModule";
import type { Search } from "../../../interfaces/Search";
import BonusXLoading from "../../BonusXLoading";
import { CardRecapPaper } from "../../common/CardRecapPaper";
import { NoResultsAlertDesktop } from "./components/NoResultsAlertDesktop";
import { NoResultsAlertMobile } from "./components/NoResultsAlertMobile";
import { isFoundNothing, isSomethingFound } from "./helper";
import { ServicesFound } from "./ServicesFound";
import goToSuggestBonusPage from "./utils/goToSuggestBonusPage";

interface ResultsProps {
  readonly results: Search.ResultsCollection;
  readonly loading: boolean;
  readonly searchText: string;
}

const CardRecapPaperNoResults = styled(CardRecapPaper)`
  max-width: ${Displaying.none} !important;
  padding: ${Spacing[2000]} ${Spacing[1500]} ${Spacing[1500]} ${Spacing[1500]} !important;
`;

export function Results({ results, loading }: ResultsProps) {
  const mutateSearch = useMutateSearch();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const goToServicesCallback = useCallback(
    function goToService(url: string) {
      mutateSearch({ visible: false, searchText: "" });
      window.scrollTo({ top: 0 });
      navigate(url);
    },
    [navigate, mutateSearch]
  );

  return (
    <Grid container alignItems="center" gap={Spacing[2000]} height="fit-content">
      {loading && (
        <Grid item xs={12}>
          <BonusXLoading />
        </Grid>
      )}

      {isSomethingFound(results.searchText, results.foundProducts) && !loading && (
        <Grid item xs={12}>
          <Grid container gap={Spacing[1000]}>
            <Grid item xs={12}>
              <Typography variant="h6">Risultati</Typography>
            </Grid>
            <Grid container gap={Spacing[500]}>
              <ServicesFound
                keyPrefix="searchServiceResult"
                goToService={goToServicesCallback}
                services={results.foundProducts}
              />
            </Grid>
          </Grid>
        </Grid>
      )}

      {isFoundNothing(results.searchText, results.foundProducts) && !loading && (
        <CardRecapPaperNoResults isMobile={isMobile} elevation={0} data-testid="no-result-wrapper">
          <Grid
            container
            justifyContent={Alignment.center}
            alignItems={Alignment.center}
            direction={Direction.column}
            css={{ gap: Spacing["1500"] }}
          >
            <Grid item>
              <SearchTextDuotone sx={{ fontSize: Spacing[3000] }} />
            </Grid>
            <Grid item>
              <Typography variant="h5">Nessun risultato</Typography>
            </Grid>
            <Grid item textAlign={Alignment.center}>
              <Typography variant="body1" color={Text.secondary}>
                Non ci sono bonus o servizi associati alla tua ricerca.
              </Typography>
            </Grid>
            <Grid item>
              {isMobile ? (
                <NoResultsAlertMobile onClick={goToSuggestBonusPage} />
              ) : (
                <NoResultsAlertDesktop onClick={goToSuggestBonusPage} />
              )}
            </Grid>
          </Grid>
        </CardRecapPaperNoResults>
      )}

      {!isEmpty(results.recentProducts) && (
        <Grid item xs={12}>
          <Grid container gap={Spacing[1000]}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Recenti</Typography>
            </Grid>
            <Grid container gap={Spacing[500]}>
              <ServicesFound
                keyPrefix="recentService"
                goToService={goToServicesCallback}
                services={results.recentProducts}
              />
            </Grid>
          </Grid>
        </Grid>
      )}

      {!isEmpty(results.popularProducts) && (
        <Grid item xs={12}>
          <Grid container gap={Spacing[1000]}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">I più popolari</Typography>
            </Grid>
            <Grid container gap={Spacing[500]}>
              <ServicesFound
                keyPrefix="popoularService"
                goToService={goToServicesCallback}
                services={results.popularProducts}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}
